import type { CartProduct } from '../types/cart';
import type { Product, UpsellItem } from '../types/product';

// Type augmentation for Window to include includeFeesInPrice
declare global {
  interface Window {
    includeFeesInPrice: (id: string) => boolean;
  }
}
// using the global function we have from https://cdn.certus.com/global/js/include-fees-in-price.js
// loaded on the page with a script tag
export const shouldIncludeFees = (id: string) =>
  typeof window.includeFeesInPrice === 'function' && id ? window.includeFeesInPrice(id) : false;

const hasNestedPricing = (item: Product | UpsellItem | CartProduct): item is Product | UpsellItem =>
  (item as Product).pricing !== undefined;

// this is only works for the Product API data product object shape
export const getDisplayPrice = (item: Product | UpsellItem | CartProduct, isUpsell: boolean = false): number => {
  let amount = 0;

  // for product API data calculation
  if (item && hasNestedPricing(item)) {
    const productId = item?.id ?? 0;
    const pricing = item?.pricing;
    const includeFees = shouldIncludeFees(productId);
    const isUpsellDiscount = pricing.discountSource === 'Upsell' && pricing?.combinedDiscount > 0;

    // if price display needs to include fees
    if (includeFees || isUpsell) {
      amount = pricing?.listPrice + pricing?.agencyFee - pricing?.combinedDiscount;
    }
    // should not include the fees in price display
    else {
      amount = pricing?.listPrice - (!isUpsellDiscount ? pricing?.combinedDiscount : 0);
    }
  }
  // for cart API data calculation
  else {
    const productId = item?.productId ?? 0;
    const includeFees = shouldIncludeFees(productId);

    if (item.unitThirdPartyFee > 0) {
      if (item.hiddenFee || includeFees) {
        // If fee is hidden, pick the appropriate price directly
        amount = item.combinedDiscount > 0 ? item.unitPriceWithDiscountAndFeeIfHidden : item.unitPriceWithUnitFeeIfHidden;
      } else {
        // Base price depends on whether it's an upsell
        const basePrice = isUpsell ? item.unitPriceAndFee : item.listPrice;
        // Apply discount if it exists
        amount = item.combinedDiscount > 0 ? basePrice - item.combinedDiscount : basePrice;
      }
    }
    // if there's no fees
    else {
      amount = item.combinedDiscount > 0 ? item.listPrice - item.combinedDiscount : item.listPrice;
    }
  }

  return amount;
};

export const getOrigPrice = (item: Product | UpsellItem | CartProduct, isUpsell: boolean = false): number => {
  let amount = 0;

  // for product API data calculation
  if (item && hasNestedPricing(item)) {
    const productId = item?.id ?? 0;
    const pricing = item?.pricing ?? 0;
    const includeFees = shouldIncludeFees(productId);

    // if price display needs to include fees
    if (includeFees || isUpsell) {
      amount = pricing.listPrice + pricing.agencyFee;
    }
    // should not include the fees in price display
    else {
      amount = pricing.listPrice;
    }
  }
  // for cart API data calculation
  else {
    const productId = item?.productId ?? 0;
    const includeFees = shouldIncludeFees(productId);
    amount =
      item.unitThirdPartyFee > 0 && (item.hiddenFee || isUpsell || includeFees) ? item.unitPriceAndFee : item.listPrice;
  }

  return amount;
};

// this is only going to work for CartProduct type
// gets the to
// export const getUpsellTotal = (upsells: CartProduct[]): number => {
//   // we are excluding any selected replace upsell from the calculations
//   // when an upsellType=Replace upsell is marked as selected it means that the main product is itself
//   // this also means that the replace upsell price is alreday included in the main item's price
//   return upsells
//     .filter((upsell) => upsell.isSelected && upsell.upsellType !== 'Replace')
//     .reduce((acc, cur) => {
//       return acc + (cur.listPrice + cur.unitThirdPartyFee - cur.combinedDiscount) * cur.quantity;
//     }, 0);
// };
